import { observer } from 'mobx-react';
import * as React from 'react';

import { TranslateFn, withI18n, WithI18nProps } from '../../../../internationalization/i18n-hoc';
import { PlotKeyToggleState } from '../../../../stores/plot-key-toggle-state';
import {
  CompetenciesLayoutItem,
  CompetencyLabelsPlacementProps,
  coordinateToTextStyle,
  plotKeyToLineStyle
} from '../../../../ui/components/competency-radar/competency-radar';
import { SVGBuilder } from '../../../../ui/components/svg-builder/svg-builder';
import { RoleBuilderHelper } from '../../../stores';

// const styles = require("./competency-radar.css")

/** Props for the component */
export interface RoleBuilderCompetencyRadarProps extends WithI18nProps {
  helper: RoleBuilderHelper;

  labelsPlacement?: CompetencyLabelsPlacementProps;

  /// The list of radius values at which the grid is drawn.
  gridRadii?: number[];

  /// Size of the SVG viewbox (coordinates are centered around 0,0)
  viewBox?: {
    width: number;
    height: number;
  };

  /// State for toggles of result sets
  // TODO: initialize so that only expected is shown
  plotKeyToggleState: PlotKeyToggleState;
  selectedCompetency?: string;
  onCompetencyClick?(key: string): void;
}

// const toggleColorMap: { [key: string]: SelectOptionProps["color"] } = {
//   // actual: "blue",
//   expected: "green",
//   // previous: "violet",
//   // group: "orange",
//   industry: "purple"
// }

/// DEBUG: const layoutDebug = false

@observer
export class RoleBuilderCompetencyRadar0 extends React.Component<RoleBuilderCompetencyRadarProps> {
  /** Renders the component */
  render() {
    const { onCompetencyClick, helper: rb, t } = this.props;

    const competenciesLayout = extractCompetenciesLayout(rb, t);

    const labelsPlacement = this.props.labelsPlacement || {
      actualScoresRadius: 120,
      competencyLabels: {
        ellipseSemiAxisX: 130,
        ellipseSemiAxisY: 130
      }
    };

    /// Use 'slice' because otherwise the plotkey toggle also toggles this off
    /// (f_k knows why)
    const gridRadii = this.props.gridRadii ? this.props.gridRadii.slice() : [20, 40, 60, 80, 100]; // ||
    // const gridRadii = [20, 40, 60, 80, 100]

    const viewBox = this.props.viewBox || {
      width: 400,
      height: 300
    };

    // the competencies angles:
    const competenciesAngles = extractCompetenciesAngles(rb);

    const atRadiusAngle = atRadiusAngleFn(competenciesAngles);

    // format points for SVG polygon (text x,y x,y)
    const coordsLabelValue = competenciesAngles.map((_a, i) => atRadiusAngle(labelsPlacement.actualScoresRadius, i));

    const builder = new SVGBuilder(1, {
      posX: (-1 * viewBox.width) / 2,
      posY: (-1 * viewBox.height) / 2,
      dimX: viewBox.width,
      dimY: viewBox.height
    });

    // Circular grid
    for (const r of gridRadii.reverse()) {
      builder.addCircle({ x: 0, y: 0, r, style: 'background' });
    }
    // Radial grid
    for (let i = 0; i < competenciesAngles.length; i += 1) {
      const pointAtAngle = atRadiusAngle(100, i);
      builder.addLine({ x1: 0, y1: 0, x2: pointAtAngle.x, y2: pointAtAngle.y, style: 'solid', stroke: 'light' });
    }

    // Labels
    for (let i = 0; i < coordsLabelValue.length; i++) {
      const competencyKey = rb.getAssignedCompetencyKeys()[i];
      const x = atRadiusAngle(100, i).x;
      const y = atRadiusAngle(100, i).y;
      const style = coordinateToTextStyle(x, y);
      const coord = coordsLabelValue[i];
      const expected = `${Math.round(rb.expected({ kind: 'competency', competencyId: competencyKey }))}`;
      const competency = competenciesLayout[i].labelTextRows.join(' ');
      builder.addLabel({
        x: coord.x,
        y: coord.y,
        anchor: style,
        // text: (!qr.areRolesSelected()) ? percentage : "",
        text: expected,
        subtext: competency.toUpperCase(),
        style: this.props.selectedCompetency === competencyKey ? 'highlighted' : 'regular',
        onClick: onCompetencyClick && (() => onCompetencyClick(competencyKey))
      });

      // if (layoutDebug) {builder.addCircle({ x: coord.x, y: coord.y, style: "filled", r: 5 })}
    }

    for (const plotkey of rb.getPlotKeys().filter(p => this.props.plotKeyToggleState.isShown(p))) {
      if (!rb.plotExists(plotkey)) {
        continue;
      }
      const scores = rb
        .getAssignedCompetencyKeys()
        .map(ckey => rb.score(plotkey, { kind: 'competency', competencyId: ckey }))
        .map(atRadiusAngle);
      // Lines
      for (let i = 0; i < scores.length - 1; i++) {
        const score = scores[i];
        const nextScore = scores[i + 1];
        builder.addLine({
          x1: score.x,
          y1: score.y,
          x2: nextScore.x,
          y2: nextScore.y,
          style: plotKeyToLineStyle(plotkey),
          stroke: 'medium'
        });
      }
      // Check scores exist - only need closing line if 3 or more vertices
      if (scores.length > 2) {
        builder.addLine({
          x1: scores[0].x,
          y1: scores[0].y,
          x2: scores[scores.length - 1].x,
          y2: scores[scores.length - 1].y,
          style: plotKeyToLineStyle(plotkey),
          stroke: 'medium'
        });
      }

      for (const score of scores) {
        if (plotkey === 'expected') {
          builder.addCircle({ x: score.x, y: score.y, r: 2, style: 'filled' });
        }
      }
      // }
    }

    return builder.build();
  }
}

export const RoleBuilderCompetencyRadar = withI18n(RoleBuilderCompetencyRadar0);

function extractCompetenciesLayout(helper: RoleBuilderHelper, t: TranslateFn): CompetenciesLayoutItem[] {
  return helper.getAssignedCompetencies().map(c => {
    const multipleSpaces = /[ ]+/g;
    const nameTrimmed = t(c.name).trim();
    const labelTextRows = nameTrimmed
      .replace(multipleSpaces, ' ')
      .replace('& ', '--&---')
      .split(' ')
      .map(row => row.replace('--&---', '& '));

    const numberRows = labelTextRows.length;
    const maxLengthRow = labelTextRows.reduce((maxValue, currentItem) => Math.max(maxValue, currentItem.length), 0);

    return {
      labelTextRows,
      maxLengthRow,
      numberRows,
      name: c.name,
      labelAdjustment: { dx: 0, dy: 0 }
    };
  });
}

function extractCompetenciesAngles(helper: RoleBuilderHelper): number[] {
  return helper.getAssignedCompetencies().map((_, i) => (i * 2.0 * Math.PI) / helper.getAssignedCompetencies().length);
}

const atRadiusAngleFn = (competenciesAngles: number[]) => (value: number, i: number) => {
  return {
    x: value * Math.sin(competenciesAngles[i]),
    y: -value * Math.cos(competenciesAngles[i])
  };
};



// WEBPACK FOOTER //
// ./src/admin/ui/components/role-builder-competency-radar/role-builder-competency-radar.tsx